<template>
    <fragment v-if="campaign">
        <div class="channel-edit-group channel-edit-group-fields">
            <div v-if="campaignEditCRMData" class="field-w">
                <b-field :label="$t('CampaignEdit.CRM.Process')">
                    <b-select expanded :placeholder="$t('CampaignEdit.CRM.ProcessPlaceholder')" v-model="campaign.CompanyProcessId"
                              @input="onProcessChange">
                        <option v-for="process in campaignEditCRMData.ProcessListData"
                                :key="`process_${process.ProcessId}`"
                                :value="+process.ProcessId">
                            {{ process.ProcessName }}
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="field-w">
                <b-field :label="$t('CampaignEdit.CRM.Status')">
                    <b-select expanded :placeholder="$t('CampaignEdit.CRM.StatusPlaceholder')" v-model="campaign.ClientStatusId"
                              :disabled="!campaign.CompanyProcessId" @input="onStatusChange">
                        <option v-for="(status, index) in statusListDataFiltered" :key="`status_${status.ProcessId}_${status.StatusId}_${index}`" :value="status.StatusId">
                            {{ status.StatusName }}
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="field-w">
                <b-field :label="$t('CampaignEdit.CRM.Assignee')">
                    <b-select expanded :placeholder="$t('CampaignEdit.CRM.AssigneePlaceholder')" v-model="campaign.ClientUserId"
                              :disabled="!campaign.CompanyProcessId || !campaign.ClientStatusId">
                        <option v-for="(assignee, index) in assigneeListDataFiltered" :key="`assignee_${assignee.ProcessId}_${assignee.StatusId}_${assignee.AssigneeId}_${index}`"
                                :value="assignee.AssigneeId">
                            {{ assignee.AssigneeName }}
                        </option>
                    </b-select>
                </b-field>
            </div>
        </div>
    </fragment>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import Fragment from "vue-fragment";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import {Getter} from "vuex-class";
import {Campaign, CampaignEditViewModel} from "@/models/campaigns/Campaign";
import {CampaignEditCRMData} from "@/models/campaigns/CampaignEditCRMData";
import {CampaignEditPageKind} from "@/models/campaigns/CampaignEditPageKind";
import {CRMAssignee} from "@/models/crm/CRMAssignee";
import {CRMStatus} from "@/models/crm/CRMStatus";
import CampaignService from "@/services/CampaignService";
import CampaignsApiService from "@/services/api/CampaignsApiService";

Vue.use(Fragment.Plugin);
const namespace = "campaignsModule";

@Component({
  components: {},
})
export default class TabCRM extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", {namespace}) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  campaignEditCRMData?: CampaignEditCRMData = {
    ClientUserId: null,
    CrmDisabled: true,
    ProcessId: null,
    ClientStatusId: null,
    AssigneeListData: [],
    ProcessListData: [],
    StatusListData: [],
  };

  CampaignEditPageKind = CampaignEditPageKind;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", {immediate: true})
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    const loader = this.$loading.show();
    CampaignsApiService.getCampaignEditCRMData(this.campaign).then((data: CampaignEditCRMData) => {
      this.campaignEditCRMData = data;
    }).finally(() => {
      loader.hide();
    });
  }

  //#endregion Watchers


  //#region Getters

  get assigneeListDataFiltered(): CRMAssignee[] {
    if (this.campaignEditCRMData) {
      return this.campaignEditCRMData.AssigneeListData.filter(
        (i: CRMAssignee) => String(i.StatusId) === String(this.campaign.ClientStatusId) && +i.ProcessId === this.campaign.CompanyProcessId
      );
    } else {
      return [];
    }
  }

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get pageKind(): CampaignEditPageKind {
    return CampaignService.getCampaignEditPageKind(this.$route.path);
  }

  get statusListDataFiltered(): CRMStatus[] {
    if (this.campaignEditCRMData) {
      return this.campaignEditCRMData.StatusListData.filter((i: CRMStatus) => +i.ProcessId === this.campaign.CompanyProcessId);
    } else {
      return [];
    }
  }

  //#endregion Getters


  //#region Handlers

  onProcessChange() {
    this.campaign.ClientStatusId = null;
    this.campaign.ClientUserId = 0;
  }

  onStatusChange() {
    this.campaign.ClientUserId = 0;
  }

  //#endregion Handlers
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.channel-edit-group {
  margin-bottom: 25px;

  &.channel-edit-group-field {
    max-width: 894px;

    .channel-edit-field {
      margin-bottom: 1rem;
    }

    .label {
      font-size: 17px;
      margin: 0;
      display: inline;
    }
  }

  &.channel-edit-group-fields {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;

    .field-w {
      padding: 0 12px 25px;
      flex-grow: 1;
      width: 50%;

      @media (max-width: $bp_md) {
        padding-bottom: 1rem;
        width: 100%;
      }
    }
  }
}
</style>
